import { FC, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Email as EmailIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';
import { clientInvoiceApi, ClientInvoiceSummary } from '../../utils/api';
import { formatDollars } from '../../utils/money';
import { sendErrorMessage } from '../../utils/sendErrorMessage';
import ClientInvoiceWizard from '../../components/ClientInvoiceWizard';

const statusColor = (s: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  switch (s) {
    case 'paid':
      return 'success';
    case 'partially_paid':
      return 'info';
    case 'sent':
    case 'pending':
    case 'processing':
      return 'warning';
    case 'overdue':
      return 'error';
    default:
      return 'default';
  }
};

const statusLabel = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// "2026-06" → "Jun 2026" for the auto-statement chip; falls back to the raw
// period string so a malformed value can't crash the row.
const periodLabel = (p?: string | null) => {
  if (!p) return '';
  try {
    return format(parseISO(`${p}-01`), 'MMM yyyy');
  } catch {
    return p;
  }
};

// List of a clinic's client statements (saved, findable) with view/print/email/pay.
// `clientId` is the Billing page's client chip: the tab used to list the whole
// clinic while the chip claimed a client (S1). The server owns the filter.
const StatementsTab: FC<{ clientId?: string }> = ({ clientId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [manageId, setManageId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<ClientInvoiceSummary[]>(
    ['client-invoices', clientId ?? null],
    () => clientInvoiceApi.list(clientId),
    { staleTime: 30_000 },
  );
  // Never trust the shape — a non-array (e.g. a route-shadowing regression) must
  // not white-screen the page.
  const statements = Array.isArray(data) ? data : [];

  async function handleDownloadPdf(s: ClientInvoiceSummary) {
    setBusyId(s.id);
    try {
      const blob = await clientInvoiceApi.getPdf(s.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Statement_${s.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message ?? 'Failed to download the PDF.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  async function handleEmail(s: ClientInvoiceSummary) {
    setBusyId(s.id);
    try {
      await clientInvoiceApi.email(s.id);
      enqueueSnackbar('Statement emailed to the client.', { variant: 'success' });
    } catch (e: any) {
      // Already surfaced the server message; the gap was the NO-response case,
      // where a 30s axios timeout said "Failed" over a send that may have gone
      // out. Same double-send harm as the invoice receipt.
      const feedback = sendErrorMessage(e, 'Could not email the statement.');
      enqueueSnackbar(feedback.message, { variant: feedback.variant });
    } finally {
      setBusyId(null);
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ overflowX: 'auto', boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Statement #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Client</TableCell>
              <TableCell align="center">Invoices</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statements.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    No statements yet — create one from the "New Invoice" split-button.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {statements.map((s) => (
              <TableRow key={s.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>
                  {s.invoiceNumber}
                  {s.origin === 'auto_monthly' && (
                    <Chip
                      label={`Auto · ${periodLabel(s.statementPeriod)}`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1, fontWeight: 400 }}
                    />
                  )}
                </TableCell>
                <TableCell>{s.createdAt ? format(parseISO(s.createdAt), 'MMM d, yyyy') : '—'}</TableCell>
                <TableCell>{s.client?.name ?? '—'}</TableCell>
                <TableCell align="center">{s.sourceInvoiceIds?.length ?? 0}</TableCell>
                <TableCell align="right">{formatDollars(s.totalAmount)}</TableCell>
                <TableCell>
                  <Chip label={statusLabel(s.status)} color={statusColor(s.status)} size="small" />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={s.status === 'paid' || s.status === 'superseded' ? 'View / print' : 'Collect payment'}>
                    <span>
                      <IconButton size="small" onClick={() => setManageId(s.id)} sx={{ color: '#8B4513' }}>
                        <PaymentIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Download PDF">
                    <span>
                      <IconButton size="small" disabled={busyId === s.id} onClick={() => handleDownloadPdf(s)}>
                        <PdfIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {/* A superseded statement was replaced by a newer month's — emailing it
                      would double-dun the client (the server rejects it too). */}
                  <Tooltip title={s.status === 'superseded' ? 'Superseded — email the current statement instead' : 'Email to client'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={busyId === s.id || s.status === 'superseded'}
                        onClick={() => handleEmail(s)}
                      >
                        <EmailIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Reuse the wizard in "manage existing statement" mode for view / pay / print / email. */}
      <ClientInvoiceWizard open={!!manageId} manageStatementId={manageId} onClose={() => setManageId(null)} />
    </>
  );
};

export default StatementsTab;
